import { useMemo } from "react";

import type { PerfRow } from "../types";

import { csvDimensionLabel, fmtCsv, hasMetric, metricVal } from "../helpers";
import { usePerformanceReportContext } from "../providers/PerformanceReportProvider";
import { buildColumns } from "../table/Columns";
import { usePerformanceReportTranslations } from "./usePerformanceReportTranslations";

/**
 * Mobile CSV export always includes every metric column regardless of the
 * shared (desktop-driven) column-visibility state — per the Jira AC5 text
 * ("Export csv downloads ... ALL columns") and the live DesignSync canvas,
 * which has no mobile column-visibility UI at all. Desktop's `columns`/
 * `csvDataFormatter` off `usePerformanceReportContext()` respect `hidden`
 * (its own `ColumnsPopover`); mobile no longer has an equivalent control
 * (see PART-3345 Jira comment), so it builds its own hidden-independent set.
 */
export const useMobileAllColumnsCsv = () => {
  const translations = usePerformanceReportTranslations();
  const { tab } = usePerformanceReportContext();

  return useMemo(() => {
    const columns = buildColumns(
      tab,
      translations.metricColumns,
      translations.sourceColumnLabels,
      translations.conversionTips,
      translations.unitUsd,
    );

    const dataFormatter = (row: PerfRow) => {
      const out: Record<string, string | number> = {
        [translations.sourceColumnLabels[tab]]: csvDimensionLabel(
          tab,
          row,
          translations.countryUnknown,
        ),
      };
      for (const c of translations.metricColumns) {
        const header = c.menuLabel ?? c.label;
        out[header] = hasMetric(row, c.key)
          ? fmtCsv(metricVal(row, c.key), c.type)
          : "";
      }
      return out;
    };

    return { columns, dataFormatter };
  }, [tab, translations]);
};
